import { aircraftQuery } from "@common/api"
import type { AircraftRowPublic } from "@common/client"
import {
  MultiSelectBody,
  type Option,
} from "@common/components/shared/multi-select-body"
import { FieldTrigger } from "@common/components/ui/field-trigger"
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@common/components/ui/popover"
import { cn } from "@common/lib/utils"
import { useQuery } from "@tanstack/react-query"
import {
  type FieldPathByValue,
  type FieldValues,
  useFormContext,
} from "react-hook-form"
import { FieldLabel } from "../fields/label"

export const FLEET = { limit: 1000 }

const TRIGGER = "justify-between"

type Props<T extends FieldValues> = {
  name: FieldPathByValue<T, string[]>
  label?: string
  optional?: boolean
}

export function aircraftOptions(fleet: AircraftRowPublic[]): Option<string>[] {
  return fleet
    .map((aircraft) => ({
      value: aircraft.id,
      label: aircraft.registration_no,
      sublabel: [aircraft.model, aircraft.serial_no]
        .filter(Boolean)
        .join(" · "),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

function summaryOf(fleet: AircraftRowPublic[], picked: string[]): string {
  return fleet
    .filter((aircraft) => picked.includes(aircraft.id))
    .map((aircraft) => aircraft.registration_no)
    .sort()
    .join(", ")
}

export function AircraftsField<T extends FieldValues>({
  name,
  label = "Aircraft",
  optional,
}: Props<T>) {
  const { control } = useFormContext<T>()
  const { data, isLoading } = useQuery(aircraftQuery(FLEET))
  const fleet = data?.data ?? []

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const picked: string[] = field.value
        return (
          <FormItem>
            <FieldLabel label={label} optional={optional} />
            <Popover onOpenChange={(open) => !open && field.onBlur()}>
              <PopoverTrigger asChild>
                <FormControl>
                  <FieldTrigger
                    ref={field.ref}
                    role="combobox"
                    aria-invalid={!!fieldState.error}
                    className={cn(
                      TRIGGER,
                      picked.length === 0 && "text-muted-foreground",
                    )}
                  >
                    <span className="truncate">
                      {summaryOf(fleet, picked) || "Select aircraft…"}
                    </span>
                  </FieldTrigger>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[--radix-popover-trigger-width] p-0"
              >
                <MultiSelectBody
                  options={aircraftOptions(fleet)}
                  initial={picked}
                  isLoading={isLoading}
                  searchPlaceholder="Search tail or model"
                  emptyLabel="No aircraft"
                  onApply={field.onChange}
                  applyOnSelect
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
